import { Project } from 'redux/projectSlice';
import { userRoles } from 'constants/constants';
import './YourSummary.scss';
import {
  useGetAverageApprovalTimeMutation,
  useGetAverageInspectionTimeMutation,
  useGetCo2StatsMutation,
  useGetIncidentContributionMutation,
  useGetInspectionContributionMutation,
  useGetObservationContributionMutation,
  useGetPaperSavedMutation,
} from 'redux/services/dashboard';
import { useCallback, useEffect } from 'react';
import { PercentValueBar } from './PercentValueBar';
import { StatsValueContainer } from './StatsValueContainer';

export type YourSummaryProps = {
  project: Project | null;
  role: string;
  userId: string;
};

export const YourSummary: React.FC<YourSummaryProps> = ({
  project,
  userId,
  role,
}) => {
  const [getPaperSaved, { data: paperSavedData, reset: resetPaperSaved }] =
    useGetPaperSavedMutation();
  const [getCo2Emission, { data: co2EmissionData, reset: resetCo2Emission }] =
    useGetCo2StatsMutation();
  const [
    getAverageInspectionTime,
    { data: avgInspectionTimeData, reset: resetAvgInspection },
  ] = useGetAverageInspectionTimeMutation();
  const [
    getAverageApprovalTime,
    { data: avgApprovalTimeData, reset: resetAvgApprovalTime },
  ] = useGetAverageApprovalTimeMutation();
  const [
    getIncidentContribution,
    { data: incidentContributionData, reset: resetIncidentContribution },
  ] = useGetIncidentContributionMutation();
  const [
    getInspectionContribution,
    { data: inspectionContributionData, reset: resetInspectionContribution },
  ] = useGetInspectionContributionMutation();
  const [
    getObservationContribution,
    { data: observationContributionData, reset: resetObservationContribution },
  ] = useGetObservationContributionMutation();

  const fetchOwnerData = useCallback(
    (projectId, userId = '') => {
      getPaperSaved({ projectId, userId });
      getCo2Emission({ projectId, userId });
      getAverageInspectionTime({ projectId, userId });
      getAverageApprovalTime({ projectId, userId });
    },
    [
      getAverageApprovalTime,
      getAverageInspectionTime,
      getCo2Emission,
      getPaperSaved,
    ],
  );

  const fetchOtherUserData = useCallback(
    (projectId, userId = '') => {
      getPaperSaved({ projectId, userId });
      getAverageInspectionTime({ projectId, userId });
      getIncidentContribution({ projectId, userId });
      getInspectionContribution({ projectId, userId });
      getObservationContribution({ projectId, userId });
    },
    [
      getAverageInspectionTime,
      getIncidentContribution,
      getInspectionContribution,
      getObservationContribution,
      getPaperSaved,
    ],
  );

  const fetchAllUserData = useCallback(
    (projectId, userId = '') => {
      getPaperSaved({ projectId, userId });
      getCo2Emission({ projectId, userId });
      getAverageInspectionTime({ projectId, userId });
      getAverageApprovalTime({ projectId, userId });
      getPaperSaved({ projectId, userId });
      getCo2Emission({ projectId, userId });
    },
    [
      getAverageApprovalTime,
      getAverageInspectionTime,
      getCo2Emission,
      getPaperSaved,
    ],
  );

  useEffect(() => {
    if (userId && project?.projectInfo.id) {
      resetAvgApprovalTime();
      resetAvgInspection();
      resetCo2Emission();
      resetIncidentContribution();
      resetInspectionContribution();
      resetPaperSaved();
      resetObservationContribution();
      if (userId === 'ALL') {
        fetchAllUserData(project.projectInfo.id, userId);
      } else if (role === userRoles.OWNER) {
        fetchOwnerData(project.projectInfo.id, userId);
      } else {
        fetchOtherUserData(project.projectInfo.id, userId);
      }
    }
  }, [userId, role, project]);

  return (
    <div className="p-3 d-flex flex-column your-summary">
      <div className="d-flex flex-column">
        {role !== userRoles.OWNER && userId !== 'ALL' && (
          <>
            <PercentValueBar
              title="Inspection"
              percent={inspectionContributionData?.percent ?? 0}
            />
            <PercentValueBar
              title="Observation"
              percent={observationContributionData?.percent ?? 0}
            />
            <PercentValueBar
              title="Incident"
              percent={incidentContributionData?.percent ?? 0}
            />
          </>
        )}
        <StatsValueContainer
          title="Paper Saved"
          value={paperSavedData?.paperSaved ?? 0}
          showRibbon
          unit="Nos."
          color="green"
        />
        {(role !== userRoles.DOER || userId === 'ALL') && (
          <StatsValueContainer
            title="CO2 Emission saved"
            value={co2EmissionData?.co2EmissionSaved}
            showRibbon
            unit="Kg"
            color="blue"
          />
        )}

        {role !== userRoles.INSPECTOR || userId === 'ALL' ? (
          <StatsValueContainer
            title="Avg. time to close the reinspection tags"
            value={avgInspectionTimeData?.avgTime}
            showRibbon
            unit="Hr"
            color="red"
          />
        ) : null}

        {role !== userRoles.DOER || userId === 'ALL' ? (
          <StatsValueContainer
            title="Avg. time taken to approve Tasks"
            value={avgApprovalTimeData?.avgTime}
            showRibbon
            unit="Hr"
          />
        ) : null}
      </div>
    </div>
  );
};
